(() => {
  if (customElements.get('eclat-video-feed')) return;

  class EclatVideoFeed extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    initialize() {
      if (this.initialized) return;

      this.grid = this.querySelector('[data-vf2-grid]');
      this.dotsWrap = this.querySelector('[data-vf2-dots]');
      this.prevButton = this.querySelector('[data-vf2-action="scroll"][data-direction="-1"]');
      this.nextButton = this.querySelector('[data-vf2-action="scroll"][data-direction="1"]');
      if (!this.grid) return;

      this.scrollRaf = null;
      this.initialized = true;

      this.addEventListener('click', this.handleClick);
      this.grid.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.syncDots();

      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                const playing = video.play();
                if (playing && playing.catch) playing.catch(() => {});
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { threshold: 0.25 },
        );
        this.grid.querySelectorAll('video').forEach((video) => this.videoObserver.observe(video));
      }
    }

    destroy() {
      if (!this.initialized) return;

      this.removeEventListener('click', this.handleClick);
      this.grid.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }

      if (this.scrollRaf !== null) cancelAnimationFrame(this.scrollRaf);
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.scrollRaf = null;
      this.initialized = false;
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const actionElement = target ? target.closest('[data-vf2-action]') : null;
      if (!actionElement || !this.contains(actionElement)) return;

      const action = actionElement.dataset.vf2Action;
      if (action === 'scroll') this.scroll(Number(actionElement.dataset.direction));
      if (action === 'dot') this.scrollToIndex(Number(actionElement.dataset.index));
    }

    handleScroll() {
      if (this.scrollRaf !== null) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.syncDots();
      });
    }

    handleResize() {
      this.syncDots();
    }

    getStep() {
      const card = this.grid.querySelector('[data-vf2-card]');
      const styles = getComputedStyle(this.grid);
      const gap = parseInt(styles.columnGap || styles.gap || '0', 10) || 0;
      return card ? card.offsetWidth + gap : this.grid.clientWidth;
    }

    scroll(direction) {
      this.grid.scrollBy({ left: direction * this.getStep(), behavior: 'smooth' });
    }

    scrollToIndex(index) {
      if (!Number.isFinite(index)) return;
      this.grid.scrollTo({ left: index * this.getStep(), behavior: 'smooth' });
    }

    syncDots() {
      const step = this.getStep();
      const maxScroll = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
      const maxIndex = step > 0 ? Math.max(0, Math.round(maxScroll / step)) : 0;
      const index = step > 0 ? Math.min(maxIndex, Math.max(0, Math.round(this.grid.scrollLeft / step))) : 0;

      if (this.dotsWrap) {
        const dots = this.dotsWrap.querySelectorAll('[data-vf2-action="dot"]');
        dots.forEach((dot, dotIndex) => {
          dot.hidden = dotIndex > maxIndex;
          dot.classList.toggle('is-active', dotIndex === index);
          dot.toggleAttribute('aria-current', dotIndex === index);
        });
      }

      if (this.prevButton) this.prevButton.classList.toggle('is-hidden', this.grid.scrollLeft <= 1);
      if (this.nextButton) this.nextButton.classList.toggle('is-hidden', this.grid.scrollLeft >= maxScroll - 1);
    }
  }

  customElements.define('eclat-video-feed', EclatVideoFeed);

  const forEachFeed = (root, callback) => {
    if (!(root instanceof Element)) return;
    if (root.matches('eclat-video-feed')) callback(root);
    root.querySelectorAll('eclat-video-feed').forEach(callback);
  };

  document.addEventListener('shopify:section:load', (event) => {
    forEachFeed(event.target, (feed) => feed.initialize());
  });

  document.addEventListener('shopify:section:unload', (event) => {
    forEachFeed(event.target, (feed) => feed.destroy());
  });
})();
